import React, {useMemo, useRef} from "react";
import {
    List,
    Button,
    ListItem,
    ListItemText,
    ListItemSecondaryAction
} from "@mui/material";
import {ArrowDropDown} from "@mui/icons-material";
import {useBooleanHistoryState} from "~/apps/core/utils";
import {makeStyles} from "@mui/styles";
import {PortTheme} from "~/ui/theme";
import {ChipInput} from "./ChipInput";
import {BottomSheet} from "../BottomSheet";
import {Success} from "~/ui/icons";


export type SelectChipInputProps<V> = {
    options: { title: string, value: V }[]
    value?: V
    onChange?: (value: V) => any
    nullOptionTitle?: string
    label?: React.ReactNode
    title?: React.ReactNode
    disabled?: boolean
    compareFunction?: (a: V, b: V) => boolean
}


const defaultProps: Partial<SelectChipInputProps<any>> = {
    compareFunction: (a, b) => a == b
}


const useStyles = makeStyles<PortTheme>(theme => ({
    item: {
        height: 48,
    }
}))


export const SelectChipInput = <T extends unknown>(props: SelectChipInputProps<T>) => {
    const {label, title, options, nullOptionTitle, value, onChange, disabled, compareFunction} = props;
    const classes = useStyles(props);
    const [open, setOpen] = useBooleanHistoryState();
    const ref = useRef<HTMLDivElement>();

    const handleClick = () => {
        ref?.current?.blur();
        setOpen(true).then(() => null);
    }

    const handleClose = () => {
        setOpen(false).then(() => null);
    }

    const handleOptionClick = (optionValue) => () => {
        setOpen(false).then(() => {
            onChange && onChange(optionValue);
        });
    }

    return (
        <>
            <ChipInput
                ref={ref}
                selected={!!value}
                color="additionalPrimary"
                onClick={handleClick}
                label={(
                    <>
                        {label || title}
                        <ArrowDropDown/>
                    </>
                )}
                disabled={disabled}
            />
            <BottomSheet title={title} open={open} onClose={handleClose}>
                <List>
                    {options.map((option, index) => (
                        <ListItem key={index} button onClick={handleOptionClick(option.value)}
                                  disabled={disabled} className={classes.item}>
                            <ListItemText primary={option.title} primaryTypographyProps={{color: "primary"}}/>
                            {compareFunction(option.value, value) && (
                                <ListItemSecondaryAction>
                                    <Success color="secondary"/>
                                </ListItemSecondaryAction>
                            )}
                        </ListItem>
                    ))}
                </List>
            </BottomSheet>
        </>
    )
}

SelectChipInput.defaultProps = defaultProps;